import {ILogic} from '@gongxigames/framework/slot/types';
import {Xhr} from '@gongxigames/framework/slot/fragments/Xhr';
import {consoleLogger, storage} from '@gongxigames/framework/core';

export interface ISessionData {
    session_name: string;
    rounds_amount: number;
    game_name: string;
    session: number[];
    ID?: number;
}

export interface ISessionEditedData {
    session_name: string;
    ID: number;
}

export class SessionsPage {
    public static readonly URL: string = 'https://sessions.rubyplay.tech/api/sessions';

    protected _gameName: string;
    protected _logic: ILogic;
    protected _sessionList!: HTMLUListElement;
    protected _editedData?: ISessionEditedData;

    constructor(gameName: string, logic: ILogic) {
        this._gameName = gameName;
        this._logic = logic;

        Xhr.get(`${SessionsPage.URL}?gameName=${this._gameName}`, (response: any) => {
            if (response.status) {
                this.init(response.sessions);
            } else {
                this.onErrorRequest(response);
            }
        }, undefined, undefined, (error: any) => this.onErrorRequest(error));
    }

    private onErrorRequest(error: any): void {
        consoleLogger.warn(error);
    }

    protected init(sessions: ISessionData[]): void {
        this.createSessionsList(sessions);
        this.initSaveBtn();
        this.initEditBtn();
        this.initCancelEditingBtn();
        this.initClearBtn();
    }

    protected createSessionsList(sessions: ISessionData[]): void {
        this._sessionList = document.querySelector('.sessionsList') as HTMLUListElement;

        for (const sessionData of sessions) {
            this.createSession(sessionData);
        }

        this.showList();
    }

    protected showList(): void {
        this._sessionList.classList.remove('hide');
        this.getElem('.noSessions').classList.add('hide');
    }

    protected createSession(data: any): void {
        const sessionElem = document.createElement('li');
        const className = data.session_name.indexOf('_') === 0 ? 'hide' : '';
        const savedSession = storage.getItem('currentSession') && JSON.parse(storage.getItem('currentSession')!);
        const isSessionChosen = savedSession && savedSession.session_name === data.session_name;
        const html = `<div class="sessionName">${data.session_name}</div>
                    <div class="sessionRounds">${data.rounds_amount}</div>
                    <div class="edit ${className}"></div>
                    <div class="delete ${className}"><div/>`;

        sessionElem.insertAdjacentHTML('beforeend', html);

        sessionElem.className = `sessionItem ${isSessionChosen ? 'chosen' : ''}`;

        this.setSessionEvents(sessionElem, data);

        this._sessionList.appendChild(sessionElem);
    }

    protected setSessionEvents(sessionElem: HTMLElement, data: ISessionData): void {
        this.getElem('.sessionName', sessionElem).onclick = () => this.setSession(sessionElem, data);
        this.getElem('.delete', sessionElem).onclick = () => this.deleteSession(sessionElem, data.ID!);
        this.getElem('.edit', sessionElem).onclick = () => this.editSession(sessionElem, data);
    }

    protected editSession(sessionElem: HTMLElement, editingData: ISessionData): void {
        const {session_name, ID} = editingData;

        sessionElem.classList.add('editable');
        document.getElementById('editSession')!.classList.remove('hide');
        this.setInputValue('sessionEditedName', session_name);

        this._editedData = {session_name, ID: ID!};
    }

    protected setSession(sessionElem: HTMLElement, session: ISessionData): void {
        this.removeChosenElem();
        sessionElem.classList.add('chosen');

        storage.setItem('currentSession', JSON.stringify(session));
    }

    protected deleteSession(sessionElem: HTMLElement, id: number): void {
        Xhr.delete(SessionsPage.URL, JSON.stringify({id}), (response: any) => {
            if (response.status) {
                storage.removeItem('currentSession');

                sessionElem.remove();
            }
        });
    }

    protected initEditBtn(): void {
        document.getElementById('editSession')!.onsubmit = (e: Event) => {
            e.preventDefault();

            if (this._editedData) {
                this._editedData.session_name = this.getInputValue('sessionEditedName');
                const serialObj = JSON.stringify(this._editedData);

                Xhr.put(SessionsPage.URL, serialObj, (response: any) => {
                    if (response.status) {
                        const sessionNameElem = this.getElem('.editable .sessionName');

                        sessionNameElem.innerHTML = this._editedData!.session_name;

                        sessionNameElem.classList.remove('editable');
                        document.getElementById('editSession')!.classList.add('hide');
                        this._editedData = undefined;
                    }
                });
            }
        };
    }

    protected initCancelEditingBtn(): void {
        document.getElementById('cancelBtn')!.onsubmit = () => document.getElementById('editSession')!.classList.add('hide');
    }

    protected initSaveBtn(): void {
        document.getElementById('saveSession')!.onsubmit = (e: Event) => {
            e.preventDefault();

            // eslint-disable-next-line @typescript-eslint/naming-convention
            const rounds_amount = +this.getInputValue('sessionRounds');
            const data: ISessionData = {
                session_name: this.getInputValue('sessionName'),
                rounds_amount,
                game_name: this._gameName,
                session: this._logic.getSession(rounds_amount)
            };
            const serialObj = JSON.stringify(data);

            Xhr.post(SessionsPage.URL, serialObj, (response: any) => {
                response.status && this.createSession(response.session);
            });
        };
    }

    protected initClearBtn(): void {
        document.getElementById('clearSessionBtn')!.onclick = () => {
            this.removeChosenElem();
            storage.removeItem('currentSession');
        };
    }

    protected removeChosenElem(): void {
        const chosenSession = this.getElem('.chosen', this._sessionList);

        chosenSession && chosenSession.classList.remove('chosen');
    }

    protected getInputValue(id: string): any {
        return (document.getElementById(id) as HTMLInputElement).value;
    }

    protected setInputValue(id: string, value: string): any {
        return (document.getElementById(id) as HTMLInputElement).value = value;
    }

    protected getElem(selector: string, parent?: HTMLElement): HTMLElement {
        let elem;

        if (parent) {
            elem = parent.querySelector(selector);
        } else {
            elem = document.querySelector(selector)!;
        }

        return elem as HTMLElement;
    }
}
